import { VStack, type StackProps } from '@chakra-ui/react';
import {
   AddToCartEventType,
   useAddToCart,
   useShopifyStorefrontClient,
} from '@ifixit/shopify-cart-sdk';
import { type ElementType } from 'react';
import { useGetUserPrice } from '../../commerce';
import type { CartImageComponent } from '../types';
import { CrossSellItem } from './cross-sell-item';
import type { CrossSellProduct } from './types';

export type { CrossSellProduct } from './types';

interface CrossSellProps {
   analytics: { eventType: AddToCartEventType };
   crossSellItems: CrossSellProduct[];
   spacing: StackProps['spacing'];
   ImageComponent: CartImageComponent;
   linkComponent?: ElementType;
   localeCode: string;
}

export function CrossSell({
   analytics,
   crossSellItems,
   spacing = '0',
   ImageComponent,
   linkComponent,
   localeCode,
}: CrossSellProps) {
   const { addToCart, enabled: addToCartEnabled } = useAddToCart(analytics);
   const { storeCode } = useShopifyStorefrontClient();

   const getUserPrice = useGetUserPrice({ storeCode });

   return (
      <VStack spacing={spacing} align="stretch">
         {crossSellItems.map(item => (
            <CrossSellItem
               key={item.itemcode}
               item={item}
               onAddToCart={addCrossSellProductToCart}
               ImageComponent={ImageComponent}
               isAddingToCart={isAddingItem(item)}
               isAddToCartDisabled={!addToCartEnabled}
               linkComponent={linkComponent}
               localeCode={localeCode}
            />
         ))}
      </VStack>
   );

   function addCrossSellProductToCart(item: CrossSellProduct) {
      const userPrice = getUserPrice(item);
      const lineItem = {
         name: item.name,
         variantTitle: 'New',
         itemcode: item.itemcode,
         shopifyVariantId: item.shopifyVariantId,
         quantity: 1,
         imageSrc: item.imageSrc,
         price: userPrice.price,
         compareAtPrice: userPrice.compareAtPrice,
         categories: item.categories,
         url: item.url,
         fulfiller: item.fulfiller,
      };
      addToCart.mutate({
         analytics: {
            type: 'product',
            currentItemCode: item.itemcode,
            handle: item.handle,
            variantid: item.shopifyVariantId,
         },
         lines: [lineItem],
      });
   }

   function isAddingItem(item: CrossSellProduct) {
      const isItem =
         addToCart.variables?.analytics.type === 'product' &&
         addToCart.variables.analytics.currentItemCode === item.itemcode;
      return addToCart.isPending && isItem;
   }
}
